import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const PrescriptionPanel = ({ onPrescribe, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDrug, setSelectedDrug] = useState(null);
  const [formData, setFormData] = useState({
    dosage: '',
    frequency: '',
    route: '',
    duration: '',
    quantity: '',
    refills: '',
    instructions: ''
  });

  const drugSearchResults = [
    {
      value: 'lisinopril-10mg',
      label: 'Lisinopril 10mg Tablet',
      description: 'ACE Inhibitor - Formulary Tier 1',
      genericName: 'Lisinopril',
      strength: '10mg',
      form: 'Tablet',
      tier: 'Tier 1',
      covered: true
    },
    {
      value: 'metformin-500mg',
      label: 'Metformin 500mg Tablet',
      description: 'Antidiabetic - Formulary Tier 1',
      genericName: 'Metformin',
      strength: '500mg',
      form: 'Tablet',
      tier: 'Tier 1',
      covered: true
    },
    {
      value: 'atorvastatin-20mg',
      label: 'Atorvastatin 20mg Tablet',
      description: 'Statin - Formulary Tier 2',
      genericName: 'Atorvastatin',
      strength: '20mg',
      form: 'Tablet',
      tier: 'Tier 2',
      covered: true
    }
  ];

  const frequencyOptions = [
    { value: 'once-daily', label: 'Once Daily' },
    { value: 'twice-daily', label: 'Twice Daily (BID)' },
    { value: 'three-times-daily', label: 'Three Times Daily (TID)' },
    { value: 'four-times-daily', label: 'Four Times Daily (QID)' },
    { value: 'every-morning', label: 'Every Morning' },
    { value: 'every-evening', label: 'Every Evening' },
    { value: 'as-needed', label: 'As Needed (PRN)' }
  ];

  const routeOptions = [
    { value: 'oral', label: 'Oral (PO)' },
    { value: 'sublingual', label: 'Sublingual (SL)' },
    { value: 'topical', label: 'Topical' },
    { value: 'intravenous', label: 'Intravenous (IV)' },
    { value: 'intramuscular', label: 'Intramuscular (IM)' },
    { value: 'subcutaneous', label: 'Subcutaneous (SC)' }
  ];

  const durationOptions = [
    { value: '7-days', label: '7 Days' },
    { value: '14-days', label: '14 Days' },
    { value: '30-days', label: '30 Days' },
    { value: '60-days', label: '60 Days' },
    { value: '90-days', label: '90 Days' },
    { value: 'ongoing', label: 'Ongoing' }
  ];

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (selectedDrug && formData?.dosage && formData?.frequency) {
      onPrescribe({
        drug: selectedDrug,
        ...formData,
        prescribedDate: new Date()?.toLocaleDateString('en-US')
      });
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
            <Icon name="Pill" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-foreground">New Prescription</h2>
            <p className="text-sm text-muted-foreground">E-Prescribing with Formulary Check</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-muted transition-smooth"
        >
          <Icon name="X" size={20} />
        </button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
        <Select
          label="Search Medication"
          description="Search by drug name or generic name"
          options={drugSearchResults}
          value={selectedDrug}
          onChange={setSelectedDrug}
          searchable
          placeholder="Type to search medications..."
          required
        />

        {selectedDrug && (
          <div className="bg-success/10 border border-success/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="CheckCircle" size={20} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-success mb-1">Insurance Coverage Verified</p>
                <p className="text-xs text-success/80">
                  This medication is covered under patient's insurance plan ({drugSearchResults?.find(d => d?.value === selectedDrug)?.tier})
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Dosage"
            type="text"
            placeholder="e.g., 10mg, 500mg"
            value={formData?.dosage}
            onChange={(e) => setFormData({ ...formData, dosage: e?.target?.value })}
            required
          />

          <Select
            label="Frequency"
            options={frequencyOptions}
            value={formData?.frequency}
            onChange={(value) => setFormData({ ...formData, frequency: value })}
            placeholder="Select frequency"
            required
          />

          <Select
            label="Route"
            options={routeOptions}
            value={formData?.route}
            onChange={(value) => setFormData({ ...formData, route: value })}
            placeholder="Select route"
            required
          />

          <Select
            label="Duration"
            options={durationOptions}
            value={formData?.duration}
            onChange={(value) => setFormData({ ...formData, duration: value })}
            placeholder="Select duration"
            required
          />

          <Input
            label="Quantity"
            type="number"
            placeholder="e.g., 30, 60, 90"
            value={formData?.quantity}
            onChange={(e) => setFormData({ ...formData, quantity: e?.target?.value })}
            required
          />

          <Input
            label="Refills"
            type="number"
            placeholder="Number of refills"
            value={formData?.refills}
            onChange={(e) => setFormData({ ...formData, refills: e?.target?.value })}
            min="0"
            max="12"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Special Instructions
          </label>
          <textarea
            className="w-full min-h-[100px] px-4 py-3 bg-background border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-smooth resize-none"
            placeholder="Enter any special instructions for the patient..."
            value={formData?.instructions}
            onChange={(e) => setFormData({ ...formData, instructions: e?.target?.value })}
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            fullWidth
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="default"
            fullWidth
            iconName="Send"
            iconPosition="left"
          >
            Send to Pharmacy
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PrescriptionPanel;